import { useIsPreviewOrDemo } from "../../contexts/IsPreviewOrDemoContext";
import {
  dropdownContainerStyles,
  dropdownListStyles,
} from "../../neutral/styles/dropdown";
import {
  SimplifiedSwipePanel,
  type SimplifiedSwipePanelRef,
} from "../../website/components/online-invitation/mobile/SimplifiedSwipePanel";
import { css } from "@emotion/react";
import { Button as FancyButton } from "@minted/fancyclothes";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverManager,
  SnackbarAlert,
  styleUtils,
  theme,
  useMediaQueryState,
  IconCalendar,
  IconClose,
} from "@minted/minted-components";
import isEmpty from "lodash/isEmpty";
import React, { memo, useCallback, useRef, useState } from "react";

export enum CalendarType {
  Apple = "apple",
  Google = "google",
  Other = "other",
  Outlook = "outlook",
}

const BUTTON_WIDTH = 215;

const styles = {
  buttonContainer: css`
    width: ${styleUtils.rem(BUTTON_WIDTH)};
  `,
  dropdownListItemText: css`
    ${theme.typeStyles.bodyMediumStyles};
    text-decoration: none;
  `,
  dropdownListWithBorder: css`
    ${dropdownListStyles};
    white-space: normal;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.gray300};
    }
  `,
  mobileOptionsContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x2};
  `,
  popoverContent: css`
    width: ${styleUtils.rem(BUTTON_WIDTH)};
  `,
  snackbar: css`
    bottom: ${styleUtils.rem(40)};
    left: 50%;
    position: fixed;
    transform: translateX(-50%);
    width: 85%;
    z-index: 1002;
  `,
  wrapper: css`
    display: flex;
    justify-content: center;
    margin-bottom: ${theme.spacing.x3};
    margin-top: ${theme.spacing.x3};
  `,
};

type CalendarTriggerButtonProps = {
  onClick: (eventArg?: React.MouseEvent) => void;
  targetRef?: React.Ref<HTMLDivElement>;
  useThemedButton: boolean;
};

const CalendarTriggerButton = ({
  onClick,
  targetRef,
  useThemedButton,
}: CalendarTriggerButtonProps) => {
  const { isPreview } = useIsPreviewOrDemo();

  if (useThemedButton) {
    return (
      <div ref={targetRef}>
        <FancyButton
          disabled={isPreview}
          onClick={onClick}
          text="Add To Calendar"
        />
      </div>
    );
  }

  return (
    <div css={styles.buttonContainer} onClick={onClick} ref={targetRef}>
      <Button
        expand
        iconAlignment="left"
        icon={IconCalendar}
        size="medium"
        text="ADD TO CALENDAR"
        type="secondary"
      />
    </div>
  );
};

type AddToCalendarProps = {
  calendarUrls: Record<string, string>;
  useThemedButton?: boolean;
};

const AddToCalendar = ({
  calendarUrls,
  useThemedButton = false,
}: AddToCalendarProps) => {
  const panelRef = useRef<SimplifiedSwipePanelRef>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  const isMobile = mediumMediaQueryState === "BELOW";

  const showSuccessSnackbar = useCallback(() => {
    setShowSnackbar(true);
  }, []);

  const dismissSnackbar = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  const handleSelect = useCallback(
    (
      calendarType: CalendarType,
      togglePopover: (eventArg?: React.MouseEvent) => void,
    ) => {
      window.open(calendarUrls[calendarType]);
      togglePopover();
      if (
        calendarType === CalendarType.Apple ||
        calendarType === CalendarType.Other
      ) {
        showSuccessSnackbar();
      }
    },
    [calendarUrls, showSuccessSnackbar],
  );

  const handleMobileSelect = useCallback(
    (calendarType: CalendarType) => {
      window.open(calendarUrls[calendarType]);
      panelRef.current?.close();
      if (
        calendarType === CalendarType.Apple ||
        calendarType === CalendarType.Other
      ) {
        showSuccessSnackbar();
      }
    },
    [calendarUrls, showSuccessSnackbar],
  );

  const handleMobileButtonClick = useCallback(() => {
    panelRef.current?.open();
  }, []);

  if (isEmpty(calendarUrls)) {
    return null;
  }

  if (isMobile) {
    return (
      <div css={styles.wrapper} data-cy="addToCalendarButton">
        <CalendarTriggerButton
          onClick={handleMobileButtonClick}
          useThemedButton={useThemedButton}
        />
        {showSnackbar && (
          <div css={styles.snackbar}>
            <SnackbarAlert
              actionIcon={IconClose}
              actionOnClick={dismissSnackbar}
              modifier="positive"
              text="Calendar file downloaded. Open it to add to your calendar."
            />
          </div>
        )}
        <SimplifiedSwipePanel ref={panelRef} title="Add to...">
          <div css={styles.mobileOptionsContainer}>
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Google)}
              text="Google Calendar"
              type="secondary"
            />
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Apple)}
              text="Apple Calendar"
              type="secondary"
            />
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Outlook)}
              text="Outlook Calendar"
              type="secondary"
            />
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Other)}
              text="Other Calendar"
              type="secondary"
            />
          </div>
        </SimplifiedSwipePanel>
      </div>
    );
  }

  // Desktop experience: Popover
  return (
    <div css={styles.wrapper} data-cy="addToCalendarButton">
      <PopoverManager position="bottom">
        {({ contentRef, isOpen, targetRef, togglePopover }) => (
          <>
            <CalendarTriggerButton
              onClick={togglePopover}
              targetRef={targetRef}
              useThemedButton={useThemedButton}
            />
            <Popover open={isOpen} ref={contentRef}>
              <PopoverContent css={styles.popoverContent}>
                <div css={dropdownContainerStyles}>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Google, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>Google Calendar</div>
                  </div>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Apple, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>Apple Calendar</div>
                  </div>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Outlook, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>
                      Outlook Calendar
                    </div>
                  </div>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Other, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>Other Calendar</div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </PopoverManager>
      {showSnackbar && (
        <div css={styles.snackbar}>
          <SnackbarAlert
            actionIcon={IconClose}
            actionOnClick={dismissSnackbar}
            modifier="positive"
            text="Calendar file downloaded. Open it to add to your calendar."
          />
        </div>
      )}
    </div>
  );
};

const MemoizedAddToCalendar = memo(AddToCalendar);

export { MemoizedAddToCalendar as AddToCalendar };

export default MemoizedAddToCalendar;
